import { ChevronUp } from "lucide-react";
import { motion } from "motion/react";

import { CampaignImage } from "@/components/campaign-card/campaign-image";
import type { CampaignAttention } from "@/campaigns";
import { campaignKey } from "@/campaigns";
import type { SwitchableCampaign } from "@/hooks/use-switchable-campaigns";
import { cn } from "@/lib/utils";

/** Covers shown before the rest collapse into "+N". */
const MAX_COVERS = 3;

/**
 * The in-game selector's "there is more" signal on phones: the OTHER campaigns' own covers,
 * overlapping like an avatar stack, instead of an abstract "+N" chip. The covers are the art the
 * player knows from the launcher, so the stack says what is waiting, not just how many. Covers
 * with news (a won prize, a jackpot that must drop) come first and carry a dot in that tone.
 *
 * Decorative inside the dock's trigger button, which owns the click and `aria-expanded`; the
 * count stays in the accessible text as "+N", the same exposure the old chip had.
 *
 * `nudge` bumps the stack once — the first-open hint (see InGameCampaignDock).
 */
export const SwitcherCoverStack = ({
  others,
  attention,
  open,
  nudge,
  className,
}: {
  others: SwitchableCampaign[];
  attention: Record<string, CampaignAttention>;
  open: boolean;
  nudge: boolean;
  className?: string;
}) => {
  const ordered = [...others].sort(
    (a, b) =>
      Number(Boolean(attention[campaignKey(b.campaign)])) -
      Number(Boolean(attention[campaignKey(a.campaign)]))
  );
  const shown =
    ordered.length > MAX_COVERS ? ordered.slice(0, MAX_COVERS) : ordered;
  const overflow = ordered.length - shown.length;

  return (
    <motion.span
      // Re-keyed per nudge so the bump replays; no-op under reduced motion (MotionConfig).
      key={nudge ? "nudge" : "rest"}
      initial={false}
      animate={nudge ? { y: [0, -5, 0, -2, 0] } : { y: 0 }}
      transition={{ duration: 0.7, ease: "easeOut" }}
      className={cn(
        "relative flex h-10 shrink-0 items-center gap-0.5 rounded-full border border-primary/55 bg-primary/15 pr-1.5 pl-1",
        className
      )}
    >
      <span className="sr-only">+{others.length}</span>
      <span className="flex items-center" aria-hidden="true">
        {shown.map(({ campaign }, i) => {
          const news = attention[campaignKey(campaign)];
          return (
            <span
              key={campaignKey(campaign)}
              className={cn("relative shrink-0", i > 0 && "-ml-2.5")}
              style={{ zIndex: shown.length - i }}
            >
              <CampaignImage
                campaign={campaign}
                hideTitle
                hideDuration
                className={cn(
                  "size-[30px] rounded-full border-2 border-background-card",
                  campaign.status === "FI" && "grayscale"
                )}
              />
              {news && (
                <span
                  className={cn(
                    "absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-background-card",
                    news.tone === "urgent" ? "bg-urgent" : "bg-prizes"
                  )}
                />
              )}
            </span>
          );
        })}
        {overflow > 0 && (
          <span className="relative -ml-2.5 flex size-[30px] shrink-0 items-center justify-center rounded-full border-2 border-background-card bg-background-card-surface font-heading text-[11px] font-extrabold text-foreground">
            +{overflow}
          </span>
        )}
      </span>
      <ChevronUp
        className={cn(
          "size-[18px] shrink-0 text-primary transition-transform duration-150",
          open && "rotate-180"
        )}
        strokeWidth={2.5}
        aria-hidden="true"
      />
    </motion.span>
  );
};
